import type { SupabaseClient } from "@supabase/supabase-js";
import { supabase as defaultClient } from "../lib/supabase";
import { shouldSkipClaim, type Achievement } from "@core/economy/achievements";
import type { AchievementDef } from "@core/economy/achievementGallery";

/** 업적 갤러리(/collection) 원천 — 정의 전체 + 내 획득 맵 */
export type AchievementGalleryRows = {
  defs: AchievementDef[];
  unlocked: Record<string, string>; // achievement_code → unlocked_at(ISO)
};

// 컬럼을 명시한다 — select('*')는 컬럼이 사라져도 조용히 undefined가 되지만, 명시하면 에러로 드러난다.
const DEF_COLUMNS = "code,name,description,category,rarity,hidden,sort_order";

/**
 * 업적 정의 + 내 획득 기록 조회 (웹 app/collection/page.js와 같은 두 쿼리).
 * - 두 테이블 SELECT는 authenticated/anon에 열려 있다(06-achievement-tables-revoke.sql: write만 회수).
 * - userId는 호출부(useAuth)에서 받는다 — auth.getUser()는 오프라인에서 락 대기로 고착될 수 있다.
 * - 미로그인(userId 없음)이면 정의만 읽고 획득 맵은 비운다(전부 잠김으로 그려진다).
 * - 실패는 throw — 빈 결과로 삼키면 화면이 "0개 획득"이라는 거짓 숫자를 그린다.
 */
export async function fetchAchievementGalleryRows(
  userId: string | null | undefined,
  client: SupabaseClient = defaultClient as any
): Promise<AchievementGalleryRows> {
  const defsQuery = client.from("achievements").select(DEF_COLUMNS).order("sort_order", { ascending: true });
  const mineQuery = userId
    ? client.from("user_achievements").select("achievement_code,unlocked_at").eq("user_id", userId)
    : Promise.resolve({ data: [], error: null });

  const [defsRes, mineRes] = await Promise.all([defsQuery, mineQuery]);
  if (defsRes.error) throw new Error(`[achievements] 정의 조회 실패: ${defsRes.error.message}`);
  if (mineRes.error) throw new Error(`[achievements] 획득 조회 실패: ${mineRes.error.message}`);

  const unlocked: Record<string, string> = {};
  for (const row of (mineRes.data ?? []) as { achievement_code: string; unlocked_at: string }[]) {
    unlocked[row.achievement_code] = row.unlocked_at;
  }
  return { defs: (defsRes.data ?? []) as AchievementDef[], unlocked };
}

export async function checkAchievements(
  action: string,
  _context: Record<string, any> = {},
  client: SupabaseClient = defaultClient as any
): Promise<Achievement[]> {
  // like_received는 글 작성자(타인) 대상 → 서버 grant_like_exp 경로에서 처리 (여기선 skip)
  if (shouldSkipClaim(action)) return [];

  const { data, error } = await client.rpc("claim_achievements_for_action", { p_action: action });
  if (error) {
    console.warn("[achievements] claim 실패:", error.message);
    return [];
  }
  const d = data as any;
  if (!d || d.ok !== true) return [];
  return Array.isArray(d.unlocked) ? (d.unlocked as Achievement[]) : [];
}

/**
 * claim 결과를 "성공 여부"까지 돌려주는 버전 (2026-09-15 · 1회성 트리거 재시도용).
 * checkAchievements는 실패와 "이미 보유"를 둘 다 []로 돌려줘 구분이 안 된다.
 * 탭 탐험가처럼 "한 번 성공하면 다시 안 부른다"는 경로는 실패 시 다음 기회에 재시도해야 하므로
 * ok=false(네트워크·RPC 오류·서버 거부)와 ok=true·unlocked=[](이미 보유)를 나눠 준다.
 */
export async function claimAchievementsForAction(
  action: string,
  client: SupabaseClient = defaultClient as any
): Promise<{ ok: boolean; unlocked: Achievement[] }> {
  if (shouldSkipClaim(action)) return { ok: true, unlocked: [] };
  try {
    const { data, error } = await client.rpc("claim_achievements_for_action", { p_action: action });
    if (error) {
      console.warn("[achievements] claim 실패:", error.message);
      return { ok: false, unlocked: [] };
    }
    const d = data as any;
    if (!d || d.ok !== true) return { ok: false, unlocked: [] };
    return { ok: true, unlocked: Array.isArray(d.unlocked) ? (d.unlocked as Achievement[]) : [] };
  } catch (e: any) {
    // 오프라인 fetch 예외 — 실패로 돌려 호출부가 재시도하게 한다
    console.warn("[achievements] claim 예외:", e?.message);
    return { ok: false, unlocked: [] };
  }
}
